import type { ProductMap, SiteExpression } from "@apf/site-publication";
import { ProofBadge } from "../components/Shell.tsx";

export function CoreLanding({ products, expression }: { products: ProductMap; expression: SiteExpression }) {
  const { hero, sections } = expression;
  const moduleCount = products.constructs.reduce((n, c) => n + c.modules.length, 0);

  const whatPartnersGet = sections.find((s) => s["id"] === "what-partners-get");
  const lifecycle = sections.find((s) => s["id"] === "lifecycle");
  const constructsSection = sections.find((s) => s["id"] === "constructs");
  const standingLabels = {
    reference: "Demonstrable",
    defined: "Defined",
    roadmap: "Roadmap",
    ...((constructsSection?.["standingLabels"] ?? {}) as Record<string, string>),
  };
  const editionsCta = sections.find((s) => s["id"] === "editions");

  return (
    <>
      {/* Hero */}
      <section className="site-hero" aria-label="WORKABLE Core introduction">
        <div className="wrap" style={{ paddingTop: 72, paddingBottom: 72 }}>
          <span className="eyebrow">{hero.eyebrow}</span>
          <h1 style={{ maxWidth: 880 }}>{hero.headline}</h1>
          <p style={{ maxWidth: 640, marginBottom: 32 }}>{hero.lede}</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <a href={hero.primaryCta.href} className="btn primary">{hero.primaryCta.label}</a>
            {hero.secondaryCta && (
              <a href={hero.secondaryCta.href} className="btn ghost">{hero.secondaryCta.label}</a>
            )}
          </div>
          {hero.creditLine && (
            <p className="small" style={{ marginTop: 28, opacity: 0.7, color: "var(--hero-text)" }}>{hero.creditLine}</p>
          )}
        </div>
      </section>

      {/* What partners get */}
      {whatPartnersGet && (
        <section className="section" aria-labelledby="partners-heading">
          <div className="wrap">
            <div className="section-head">
              <h2 id="partners-heading">{whatPartnersGet.title}</h2>
              <p>{whatPartnersGet.body}</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 24 }}>
              {((whatPartnersGet["items"] ?? []) as Array<{ id: string; title: string; body: string }>).map((item) => (
                <div key={item.id} style={{ borderTop: "2px solid var(--brand)", paddingTop: 20 }}>
                  <h3 style={{ fontSize: 18, marginBottom: 10 }}>{item.title}</h3>
                  <p style={{ fontSize: 14, color: "var(--muted)" }}>{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* World → Construct → Workblock */}
      {lifecycle && (
        <section className="section" aria-labelledby="lifecycle-heading">
          <div className="wrap">
            <h2 id="lifecycle-heading">{lifecycle.title}</h2>
            <div className="grid-3" style={{ gap: 32, marginTop: 32 }}>
              {Object.entries(products.lifecycle).map(([key, text], i) => (
                <div key={key} style={{ borderTop: "2px solid var(--line)", paddingTop: 20 }}>
                  <span className="small" style={{ display: "block", marginBottom: 12 }}>0{i + 1} / {key.charAt(0).toUpperCase() + key.slice(1)}</span>
                  <p style={{ fontSize: 15, color: "var(--muted)" }}>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* The nine Constructs */}
      <section className="section" aria-labelledby="constructs-heading">
        <div className="wrap">
          <div className="section-head">
            <h2 id="constructs-heading">{constructsSection?.title ?? "Nine products. One model."}</h2>
            <p>{constructsSection?.body ?? ""}</p>
          </div>
          {/* Module totals summary */}
          <p className="small" style={{ marginBottom: 24 }}>
            {(["reference", "defined", "roadmap"] as const).map((standing) => (
              <span key={standing} title={products.standingVocabulary[standing]}>
                {products.moduleTotals[standing]} {standingLabels[standing]} ·{" "}
              </span>
            ))}
            {moduleCount} modules
          </p>
          <div className="product-grid" data-testid="construct-grid">
            {products.constructs.map((c, i) => (
              <a key={c.id} href={`/constructs/${c.slug}`} className="product-card">
                <span className="ordinal">0{i + 1}</span>
                <span className="sig">{c.signature}</span>
                <h3>{c.functionLabel}</h3>
                <p>{c.descriptor}</p>
                <div className="card-bottom">
                  {/* Module standing bar */}
                  <div className="module-bar" aria-label={`${c.modules.length} modules`}>
                    {c.modules.map((m) => (
                      <div key={m.id} className={`module-pip ${m.standing}`} title={m.name + ": " + m.standing} />
                    ))}
                  </div>
                  <ProofBadge proofClass={c.proofClass} />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Editions CTA */}
      {editionsCta && (
        <section className="section" aria-labelledby="editions-heading" style={{ borderBottom: "none" }}>
          <div className="wrap" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 32 }}>
            <div>
              <h2 id="editions-heading" style={{ margin: 0, maxWidth: 600 }}>{editionsCta.title}</h2>
              <p style={{ marginTop: 16, maxWidth: 520, color: "var(--muted)" }}>{editionsCta.body}</p>
            </div>
            {editionsCta["cta"] ? (
              <a href={(editionsCta["cta"] as { href: string }).href} className="btn primary">
                {(editionsCta["cta"] as { label: string }).label}
              </a>
            ) : null}
          </div>
        </section>
      )}
    </>
  );
}
